// Design A ("Corporate Band") Sales Invoice -- used ONLY for Print Preview
// (via PrintSalesVoucher.jsx / SalesVoucherPrint.jsx) when this company's
// institution_profile.print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/salesInvoiceAdapter.js's config mapping
// exactly, so Print Preview matches the downloaded PDF/WhatsApp share
// pixel-for-pixel. SalesVoucherBody.jsx (the legacy component, still used
// for the live entry-screen preview and for the 'legacy' print_template) is
// completely untouched.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, numberFormatTax, formatToCompanyTimezone } from "../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../lib/designAWords";
import { fitItemName } from "../../../lib/designAItemFit";
import { formatPercent, resolveItemDiscountAmount, formatItemDiscountCell } from "../../../lib/designANumberFormat";
import { salesRecordWithDetailsApi } from "../../../apiservice/sales";
import { getSundryDebitorBalanceApi, getPrimaryBankAccountApi, getBankAccountByIdsApi } from "../../../apiservice/accounts";
import { getInstitutionApi } from "../../../apiservice/setting";
import { bankAccountToConfig } from "../../../lib/designABankAccount";
import { selectAuthInfo } from "../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../components/common/VoucherBodyDesignA";
import { buildSalesInsuranceConfigDesignA } from "./salesInsuranceConfigDesignA";

const isBonusItem = (item) => Number(item.item_rate) === 0;

function mergeVoucherItems(items) {
  const map = {};
  items.forEach((item) => {
    const key = [
      item.item_id, item.item_description || "", item.batch_id || 0, item.packing_name || "",
      item.hsn_sac_code || "", Number(item.item_rate || 0).toFixed(2), Number(item.item_discount_per || 0),
      Number(item.item_tax_per || 0), Number(item.item_rate) === 0 ? "BONUS" : "NORMAL",
    ].join("|");
    if (!map[key]) {
      map[key] = { ...item, item_qty: Number(item.item_qty || 0), item_total: Number(item.item_total || 0) };
    } else {
      map[key].item_qty += Number(item.item_qty || 0);
      map[key].item_total += Number(item.item_total || 0);
    }
  });
  return Object.values(map);
}

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional batch/description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as many
// rows fit per page. Mirrors salesInvoiceAdapter.js exactly.
const SALES_INVOICE_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as salesInvoiceAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
// disc/tax widened from an earlier, tighter pass: their header labels
// ("DISC %" / "VAT %") were wrapping onto two lines at 44-46px, and disc
// also needs headroom for a discount AMOUNT (not just a %), which can run
// several digits wider than a percentage.
//
// rate/hsn/amount widened again after a real multi-page print showed a
// 6-digit rate ("1,14,999.00", 11 chars) getting clipped with "…" at the
// old 74px -- a number must never be allowed to truncate (unlike the item
// name, which has an explicit shrink-then-truncate fallback by design).
// qty bumped back up from an in-between 38px -- narrow enough to fit a
// quantity value but too narrow for its own "QTY" header, which a real
// print showed clipped to "Q…". Headers must never truncate, same rule
// already applied to every other column.
// disc bumped from 64 to 72 -- the header itself ("Disc", mode-neutral now)
// needs less room than "DISC %" did, but a flat-amount discount value
// (e.g. "1,234.50") needs more room than a 1-2 digit percentage ever did.
const COL_WIDTH = { hash: 36, hsn: 72, rate: 100, qty: 44, disc: 72, tax: 54, amount: 104 };
const CELL_PADDING = 16;
// Deliberately on the generous side (lower px/char than a safe numeric
// estimate) -- overshooting just means the name shrinks a bit more via
// fitItemName's own font-scaling, whereas undershooting means truncating
// well before the name actually runs out of real room (the bug a real
// print caught).
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

function ItemCell({ name, maxChars, batchName, description }) {
  const subParts = [];
  if (batchName) subParts.push(`Batch: ${batchName}`);
  if (description) subParts.push(description);
  return (
    <div className="da-item-cell">
      <ItemNameCell name={name} maxChars={maxChars} />
      {subParts.length > 0 && <div className="da-item-sub">{subParts.join(" · ")}</div>}
    </div>
  );
}

export default function SalesVoucherBodyDesignA({ sale_id, hideBonusItems = false, showStamp = false, showSignature = false, showShipping = false }) {
  const [sale, setSale] = useState(null);
  const [accDue, setAccDue] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const [paymentBankAccount, setPaymentBankAccount] = useState(null);

  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!sale_id) return;
    salesRecordWithDetailsApi({ sale_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setSale(res.data[0]);
    });
  }, [sale_id]);

  useEffect(() => {
    if (!sale) return;
    getSundryDebitorBalanceApi({ customerId: sale.acc_id }).then((res) => {
      if (res?.data?.accounts?.length) setAccDue(res.data.accounts[0].balance);
    });
  }, [sale]);

  // This voucher's own payment can be received into a bank account that
  // isn't marked primary -- when that's the case, show the account the
  // money actually went into, not an unrelated "primary" one (see
  // getBankAccountByIds's comment in backend/src/controllers/accounts.js).
  useEffect(() => {
    if (!sale) return;
    const toAccIds = (sale.trans || []).map((t) => t.to_acc_id).filter(Boolean);
    if (!toAccIds.length) return;
    getBankAccountByIdsApi(toAccIds).then((res) => setPaymentBankAccount(res?.data?.data || null)).catch(() => {});
  }, [sale]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!sale) return null;

  const bankDetails = bankAccountToConfig(paymentBankAccount || primaryBankAccount);

  // Insurance/patient-billed sales get their own layout, matching the same
  // branch administration.js's generateSalesInvoicePdfFast makes server-side
  // (buildSalesInsuranceBodyDesignA vs. buildSalesInvoiceBodyDesignA) --
  // decided here, after fetching, rather than by the caller ahead of time.
  if (sale.insurance_id) {
    const insuranceConfig = buildSalesInsuranceConfigDesignA({ sale, accDue, authInfo, institutionData, institution, bankDetails, showStamp, showSignature, showShipping });
    return <VoucherBodyDesignA config={insuranceConfig} />;
  }

  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";

  const isIndividual = sale.is_cal_type === "individual";
  const rawItems = sale.details || [];
  const items = mergeVoucherItems(rawItems);
  const visibleItems = hideBonusItems ? items.filter((d) => !isBonusItem(d)) : items;
  const hasBonusItems = items.some(isBonusItem);

  // HSN/SAC is optional on the item master -- show the column only when at
  // least one item on this voucher actually has a code, otherwise it's a
  // whole column of blank cells wasting space that the item name could use.
  const showHSN = isIndia && isIndiaGst && items.some((d) => d.hsn_sac_code);

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number(d.item_qty || d.sale_qty || 0);
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = resolveItemDiscountAmount(d, base);
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;
      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      acc.totalQty += qty;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0, totalQty: 0 }
  );

  const totalTax = isIndividual ? totals.taxTotal : sale.total_tax;
  const serviceCost = Number(sale.other_service_cost || 0);
  const grandTotalForWords = isIndividual ? totals.grandTotal + serviceCost : (sale.total_amount || 0) + serviceCost;

  const accMgrLabel = isUAE ? "Rep." : "Account Manager";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const useBranch = !!authInfo?.branch_info;
  const logoUrl = useBranch ? institution?.branch_logo : institution?.logo;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  // No dedicated "Batch" column -- matches the legacy design, which never
  // had one either: batch_name (when present) and item_description (when
  // present) both go as sub-lines under the item name instead, not as
  // their own always-visible column with a "—" placeholder on every row
  // that has no batch.
  const usedWidth =
    COL_WIDTH.hash + (showHSN ? COL_WIDTH.hsn : 0) + COL_WIDTH.rate + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.disc : 0) + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item & Description", cellClassName: "da-item-td" },
  ];
  if (showHSN) columns.push({ label: "HSN/SAC", width: `${COL_WIDTH.hsn}px` });
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Qty", align: "right", width: `${COL_WIDTH.qty}px` });
  // Not "Disc %" -- a row's discount can be entered as either a % or a
  // flat amount (discount_mode), so the header stays mode-neutral and each
  // row's own value (formatItemDiscountCell) says which one it is.
  if (isIndividual) columns.push({ label: "Disc", align: "right", width: `${COL_WIDTH.disc}px` });
  if (isIndividual && !isGSTDisabled) columns.push({ label: `${taxLabel} %`, align: "right", width: `${COL_WIDTH.tax}px` });
  columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });

  const rows = visibleItems.map((d, i) => {
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} batchName={d.batch_name} description={d.item_description} />,
    ];
    if (showHSN) cells.push(d.hsn_sac_code);
    cells.push(<span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>);
    cells.push(<span className="da-num-cell" key="q">{formatPercent(d.item_qty)}</span>);

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="d">{formatItemDiscountCell(d, numberFormat)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      let total = Number(d.item_qty || 0) * Number(d.item_rate || 0);
      total -= resolveItemDiscountAmount(d, total);
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: isBonusItem(d), rawAmount };
  });

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(isIndividual ? totals.subTotal : sale.sub_total) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(isIndividual ? totals.discountTotal : sale.total_discount)}`, credit: true });

  let taxSummary = null;
  if (isIndiaGst) {
    if (sale.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormatTax(sale.cgst_amount) });
      totalsRows.push({ label: "SGST", value: numberFormatTax(sale.sgst_amount) });
      taxSummary = {
        mode: "cgst_sgst",
        rows: [{ rate: `${sale.item_tax_per || ""}%`, taxable: numberFormat(totals.subTotal - totals.discountTotal), cgst: numberFormatTax(sale.cgst_amount), sgst: numberFormatTax(sale.sgst_amount) }],
      };
    }
    if (sale.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormatTax(sale.igst_amount) });
      taxSummary = {
        mode: "igst",
        rows: [{ rate: `${sale.item_tax_per || ""}%`, taxable: numberFormat(totals.subTotal - totals.discountTotal), igst: numberFormatTax(sale.igst_amount) }],
      };
    }
    if (sale.gst_calculated === "no") {
      totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(totalTax) });
    }
  } else if (!isIndia) {
    totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(totalTax) });
  }

  if (Number(sale.other_service_cost) > 0) {
    totalsRows.push({ label: "Additional Expense", value: numberFormat(sale.other_service_cost) });
  }

  const config = {
    docTitle: "TAX INVOICE",
    docNumber: sale.sale_voucher_no,
    institution: { logoUrl, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Bill To",
      billTo: {
        name: sale.acc_name,
        lines: [
          sale.acc_mgr ? `${accMgrLabel}: ${sale.acc_mgr}` : null,
          sale.address ? `Address: ${sale.address}` : null,
          sale.contact_no ? `Contact No: +${sale.contact_no}` : null,
          sale.vat_no ? `${taxRegLabel}: ${sale.vat_no}` : null,
          sale.licence_no ? `License No: ${sale.licence_no}` : null,
        ].filter(Boolean),
      },
      shipTo:
        showShipping && sale.shipping_address
          ? {
              lines: [
                sale.shipping_contact_person ? `Contact Person: +${sale.shipping_contact_person}` : null,
                `Address: ${sale.shipping_address}`,
                sale.shipping_po_box ? `Po Box: ${sale.shipping_po_box}` : null,
                sale.shipping_contact_no ? `Contact No: +${sale.shipping_contact_no}` : null,
              ].filter(Boolean),
            }
          : null,
      fromLabel: "From",
      from: {
        name: companyName,
        lines: [
          institutionData?.street_address || null,
          institutionData?.phone_no ? `+${institutionData.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Invoice Details",
      meta: [
        { label: "Invoice Date", value: (formatToCompanyTimezone(sale.created_date) || "").split(" ")[0] },
        { label: "Due Date", value: sale.due_date || "On receipt" },
        ...(institutionData?.vat_license ? [{ label: taxRegLabel, value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: visibleItems.length,
    totalQty: totals.totalQty,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(sale.total_amount),
    paidValue: numberFormat(sale.paid_total || 0),
    dueValue: numberFormat(sale.due_amount || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotalForWords, { country, currency }),
    bankDetails,
    taxSummary,
    terms: institutionData?.invoice_default_terms || "Thanks for your business. Goods once sold will not be taken back.",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: hasBonusItems ? "*Bonus items are supplied free of cost and not included in taxable value." : null,
    rowBudgets: SALES_INVOICE_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return <VoucherBodyDesignA config={config} />;
}
